import axiosServices from "../utils/axios";

import type {
  ShiftAssignment,
  ShiftType,
  TimeOffEntry,
  TimeOffType,
} from "../utils/types/staffDutyCalendar";

export interface RawPerson {
  person_id: string;
  name: string;
  email: string;
}

export interface RawShiftAssignment {
  shift_assignment_id: string;
  date: string;
  shift: string;
  person_id: string;
  created_dt: string;
  created_by: string | null;
  modified_dt: string;
  modified_by: string | null;
  person?: RawPerson;
}

export interface RawTimeOffEntry {
  time_off_entry_id: string;
  request_id?: string | null;
  revision?: number;
  source?: "approved_request" | "legacy_confirmed";
  person_id: string;
  type: string;
  start_date: string;
  end_date: string;
  notes: string;
  created_dt: string;
  created_by: string | null;
  modified_dt: string;
  modified_by: string | null;
  person?: RawPerson;
}

// Transform backend response to frontend ShiftAssignment format
const transformShiftAssignment = (raw: RawShiftAssignment): ShiftAssignment => ({
  id: raw?.shift_assignment_id || "",
  date: raw?.date || "",
  shift: raw?.shift as ShiftType,
  personId: raw?.person_id || "",
});

// Transform backend response to frontend TimeOffEntry format
const transformTimeOffEntry = (raw: RawTimeOffEntry): TimeOffEntry => ({
  id: raw?.time_off_entry_id || "",
  personId: raw?.person_id || "",
  type: raw?.type as TimeOffType,
  startDate: raw?.start_date || "",
  endDate: raw?.end_date || "",
  notes: raw?.notes ?? "",
  createdAt: raw?.created_dt || "",
  requestId: raw?.request_id || null,
  revision: raw?.revision,
  source: raw?.source,
});

export const fetchStaffDutyCalendar = async (
  _agentIds: string[] = [],
  startDate?: string,
  endDate?: string
): Promise<{ assignments: ShiftAssignment[]; timeOff: TimeOffEntry[] }> => {
  const params: Record<string, string> = {};

  if (startDate) {
    params.startDate = startDate;
  }
  if (endDate) {
    params.endDate = endDate;
  }
  // if (agentIds.length > 0) {
  //   params.agentIds = agentIds.join(",");
  // }

  const response = await axiosServices.get<{
    data?: {
      assignments?: RawShiftAssignment[];
      timeOff?: RawTimeOffEntry[];
    };
    assignments?: RawShiftAssignment[];
    timeOff?: RawTimeOffEntry[];
  }>("/staff-duty-calendar", { params });

  const payload = response.data?.data || response.data || {};
  const rawAssignments = payload.assignments || [];
  const rawTimeOff = payload.timeOff || [];

  return {
    assignments: rawAssignments.map(transformShiftAssignment),
    timeOff: rawTimeOff.map(transformTimeOffEntry),
  };
};

const withServerMessage = async <T>(
  request: () => Promise<T>,
  fallback: string
): Promise<T> => {
  try {
    return await request();
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || fallback);
  }
};

export const addShiftAssignment = async (data: {
  date: string;
  shift: ShiftType;
  personId: string;
}): Promise<ShiftAssignment> => {
  const response = await withServerMessage(
    () =>
      axiosServices.post<{
        data?: RawShiftAssignment;
      }>("/staff-duty-calendar/assignments", data),
    "Failed to add shift assignment."
  );

  const payload = (response.data?.data || response.data) as RawShiftAssignment;
  return transformShiftAssignment(payload);
};

export const removeShiftAssignment = async (
  id: string
): Promise<{ id: string }> => {
  await axiosServices.delete(`/staff-duty-calendar/assignments/${id}`);
  return { id };
};

export interface TeamAssignmentResult {
  created: ShiftAssignment[];
  addedCount: number;
  alreadyAssignedCount: number;
  ineligibleCount: number;
  totalMembers: number;
  message: string;
}

export const addTeamAssignment = async (data: {
  date: string;
  shift: ShiftType;
  teamId: string;
}): Promise<TeamAssignmentResult> => {
  const response = await withServerMessage(
    () =>
      axiosServices.post<{
        data?: {
          created?: RawShiftAssignment[];
          addedCount?: number;
          alreadyAssignedCount?: number;
          ineligibleCount?: number;
          totalMembers?: number;
        };
        message?: string;
      }>("/staff-duty-calendar/assignments/team", data),
    "Failed to add team to lane."
  );

  const payload = response.data?.data || {};
  return {
    created: (payload.created || []).map(transformShiftAssignment),
    addedCount: payload.addedCount || 0,
    alreadyAssignedCount: payload.alreadyAssignedCount || 0,
    ineligibleCount: payload.ineligibleCount || 0,
    totalMembers: payload.totalMembers || 0,
    message: response.data?.message || "",
  };
};
